import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { AZURE } from '../services/api';
import { VUKZ } from '../theme/vukzTheme';
import { Clock, ArrowRight, Search, ArrowLeft } from 'lucide-react';
import { getSportBarGradient } from '../config/sportsConfig';
import SEOHead from '../components/seo/SEOHead';

/**
 * PublicInsightsListPage — Listagem minimalista de Estudos
 * Route: /estudos
 * Design: VUKZ-minimal — fundo branco, tipografia forte, sem ruído visual.
 */

const SPORT_TAG = {
  swimming: '🏊 Natação',
  football: '⚽ Futebol',
  general:  '📊 Geral',
};

/* ─── Study Card ─── */
const StudyCard = ({ article, large = false }) => {
  const sport = SPORT_TAG[article.sport] || SPORT_TAG.general;
  const isStudy = article.category === 'study';

  return (
    <Link
      to={`/estudos/${article.slug}`}
      className={`group block ${large ? 'md:col-span-2' : ''}`}
    >
      <article
        className="h-full bg-white rounded-2xl border border-gray-100 hover:border-gray-300
                   transition-all duration-300 hover:shadow-sm overflow-hidden flex flex-col"
      >
        {/* Thin accent top bar — gradient by sport */}
        <div
          className="h-1.5 w-full"
          style={{ background: getSportBarGradient(article.sport) }}
        />

        <div className="p-6 flex flex-col flex-1">
          {/* Tags row */}
          <div className="flex items-center gap-2 mb-4">
            <span className="text-[11px] font-semibold uppercase tracking-widest text-gray-400">
              {sport}
            </span>
            {isStudy && (
              <span className="text-[11px] font-semibold uppercase tracking-widest text-indigo-500">
                · Estudo
              </span>
            )}
            {article.is_featured && (
              <span className="ml-auto text-[11px] font-bold uppercase tracking-widest text-amber-500">
                Destaque
              </span>
            )}
          </div>

          {/* Title */}
          <h2
            className={`font-bold text-gray-900 group-hover:text-gray-600 transition-colors
                         leading-snug ${large ? 'text-xl' : 'text-base'}`}
          >
            {article.title}
          </h2>

          {/* Excerpt */}
          {article.excerpt && (
            <p className={`text-gray-400 mt-3 line-clamp-2 ${large ? 'text-sm' : 'text-xs'}`}>
              {article.excerpt}
            </p>
          )}

          {/* Spacer */}
          <div className="flex-1" />

          {/* Footer */}
          <div className="flex items-center justify-between mt-6 pt-4 border-t border-gray-50">
            <span className="text-[11px] text-gray-300 flex items-center gap-1">
              <Clock className="w-3 h-3" />
              {article.published_at
                ? new Date(article.published_at).toLocaleDateString('pt-BR', {
                    day: '2-digit',
                    month: 'short',
                    year: 'numeric',
                  })
                : '—'}
            </span>
            <span
              className="text-xs font-semibold flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity"
              style={{ color: VUKZ.primary }}
            >
              Ler <ArrowRight className="w-3 h-3" />
            </span>
          </div>
        </div>
      </article>
    </Link>
  );
};

/* ─── Main Page ─── */
const PublicInsightsListPage = () => {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sportFilter, setSportFilter] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [offset, setOffset] = useState(0);
  const [total, setTotal] = useState(0);
  const LIMIT = 12;

  const fetchArticles = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: LIMIT, offset };
      if (sportFilter) params.sport = sportFilter;
      const { data } = await AZURE.get('/editorial/public/articles', { params });
      setArticles(data.data || []);
      setTotal(data.total || 0);
    } catch (err) {
      console.error('Error fetching public articles:', err);
    } finally {
      setLoading(false);
    }
  }, [sportFilter, offset]);

  useEffect(() => { fetchArticles(); }, [fetchArticles]);

  const filtered = searchTerm
    ? articles.filter((a) =>
        a.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (a.excerpt || '').toLowerCase().includes(searchTerm.toLowerCase())
      )
    : articles;

  return (
    <div className="min-h-screen bg-white">
      <SEOHead
        title="Estudos"
        description="Estudos e análises esportivas com inteligência artificial. Natação, atletismo e futebol."
        path="/estudos"
      />
      {/* ── Sticky navbar ── */}
      <nav className="sticky top-0 z-50 bg-white/80 backdrop-blur-md border-b border-gray-100">
        <div className="max-w-5xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <img src="/vukz-logo.jpeg" alt="VUKZ" className="h-8 w-auto" />
          </Link>

          <Link
            to="/"
            className="text-sm font-medium text-gray-400 hover:text-gray-900 transition-colors flex items-center gap-1"
          >
            <ArrowLeft className="w-3.5 h-3.5" />
            Início
          </Link>
        </div>
      </nav>

      {/* ── Hero ── */}
      <header className="max-w-5xl mx-auto px-6 pt-16 pb-12">
        <p
          className="text-[11px] font-semibold uppercase tracking-[0.2em] mb-4"
          style={{ color: VUKZ.textLight }}
        >
          Estudos · VUKZ
        </p>
        <h1 className="text-4xl md:text-5xl font-black text-gray-900 tracking-tight leading-[1.05]">
          Estudos Esportivos
        </h1>
        <p className="text-base text-gray-400 mt-4 max-w-xl leading-relaxed">
          Análises aprofundadas baseadas em dados reais de competições.
          Natação, futebol e mais — gerados pela plataforma VUKZ.
        </p>

        {/* Search + filters */}
        <div className="mt-8 flex flex-col sm:flex-row items-start sm:items-center gap-3">
          <div className="relative flex-1 max-w-md">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-300" />
            <input
              type="text"
              placeholder="Buscar estudos..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-gray-200
                         text-sm text-gray-900 placeholder-gray-300
                         focus:outline-none focus:ring-2 focus:ring-gray-200 focus:border-gray-300"
            />
          </div>

          <div className="flex items-center gap-2">
            {['', 'swimming', 'football'].map((s) => (
              <button
                key={s}
                onClick={() => { setSportFilter(s); setOffset(0); }}
                className={`px-3 py-2 rounded-lg text-xs font-semibold transition-all ${
                  sportFilter === s
                    ? 'bg-gray-900 text-white'
                    : 'text-gray-400 hover:text-gray-600 hover:bg-gray-50'
                }`}
              >
                {s === '' ? 'Todos' : s === 'swimming' ? '🏊 Natação' : '⚽ Futebol'}
              </button>
            ))}
          </div>

          <span className="text-[11px] text-gray-300 ml-auto hidden sm:inline">
            {total} {total === 1 ? 'estudo' : 'estudos'}
          </span>
        </div>
      </header>

      {/* ── Featured interactive study ── */}
      <section className="max-w-5xl mx-auto px-6 mb-10">
        <Link
          to="/estudos/interativo/evolucao-tempos"
          className="group block rounded-2xl border border-gray-100 hover:border-gray-300
                     transition-all duration-300 hover:shadow-sm overflow-hidden"
        >
          <div className="flex flex-col md:flex-row">
            <div
              className="w-full md:w-44 h-24 md:h-auto flex flex-col items-center justify-center flex-shrink-0 relative overflow-hidden"
              style={{ background: 'linear-gradient(135deg, #0c4a6e 0%, #0369a1 40%, #0ea5e9 100%)' }}
            >
              <div className="absolute inset-0" style={{ background: 'repeating-linear-gradient(90deg, rgba(255,255,255,0.03) 0px, rgba(255,255,255,0.03) 2px, transparent 2px, transparent 40px)' }} />
              <p className="relative text-[8px] font-bold uppercase tracking-[0.3em] text-white/30 mb-0.5">VUKZ</p>
              <span className="relative text-2xl opacity-20">🏊</span>
              <p className="relative text-[8px] font-semibold uppercase tracking-[0.15em] text-white/40">Data Study</p>
              <p className="relative text-[11px] font-black text-white/60 tracking-wider">NATAÇÃO</p>
            </div>
            <div className="p-6 flex-1">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-[11px] font-semibold uppercase tracking-widest text-gray-400">
                  🏊 Natação
                </span>
                <span className="text-[11px] font-bold uppercase tracking-widest text-emerald-500">
                  · Interativo
                </span>
              </div>
              <h3 className="text-lg font-bold text-gray-900 group-hover:text-gray-600 transition-colors">
                Evolução do Tempo Médio por Prova — Natação Brasil
              </h3>
              <p className="text-sm text-gray-400 mt-2 max-w-lg">
                Gráficos interativos de tempos médios em todas as provas, por gênero e categoria etária.
              </p>
              <span
                className="inline-flex items-center gap-1 mt-4 text-xs font-semibold"
                style={{ color: VUKZ.primary }}
              >
                Explorar estudo <ArrowRight className="w-3 h-3" />
              </span>
            </div>
          </div>
        </Link>
      </section>

      {/* ── Grid ── */}
      <section className="max-w-5xl mx-auto px-6 pb-20">
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div key={i} className="bg-gray-50 h-64 rounded-2xl animate-pulse" />
            ))}
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-20">
            <p className="text-gray-400 text-lg">Nenhum estudo encontrado.</p>
            <p className="text-gray-300 text-sm mt-2">
              Estamos preparando análises aprofundadas. Volte em breve.
            </p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {filtered.map((article, idx) => (
                <StudyCard
                  key={article.id}
                  article={article}
                  large={idx === 0 && offset === 0}
                />
              ))}
            </div>

            {/* Pagination */}
            {total > LIMIT && (
              <div className="flex justify-center items-center gap-4 mt-12">
                <button
                  onClick={() => setOffset(Math.max(0, offset - LIMIT))}
                  disabled={offset === 0}
                  className="px-4 py-2 rounded-lg text-xs font-semibold text-gray-400
                             hover:text-gray-900 disabled:opacity-30 transition-colors"
                >
                  ← Anterior
                </button>
                <span className="text-xs text-gray-300">
                  {Math.floor(offset / LIMIT) + 1} / {Math.ceil(total / LIMIT)}
                </span>
                <button
                  onClick={() => setOffset(offset + LIMIT)}
                  disabled={offset + LIMIT >= total}
                  className="px-4 py-2 rounded-lg text-xs font-semibold text-gray-400
                             hover:text-gray-900 disabled:opacity-30 transition-colors"
                >
                  Próxima →
                </button>
              </div>
            )}
          </>
        )}
      </section>

      {/* ── Footer ── */}
      <footer className="border-t border-gray-100 py-8">
        <div className="max-w-5xl mx-auto px-6">
          <div className="flex items-center justify-between">
            <img src="/vukz-logo.jpeg" alt="VUKZ" className="h-5 w-auto opacity-30" />
            <p className="text-[11px] text-gray-300">
              Data Intelligence · Análise aprofundada
            </p>
          </div>
          <div className="flex items-center justify-center gap-2 mt-4 pt-4 border-t border-gray-50">
            <img
              src="/microsoft-logo.png"
              alt="Microsoft for Startups"
              className="h-4 w-auto opacity-30"
              onError={(e) => { e.target.style.display = 'none'; }}
            />
            <p className="text-[10px] text-gray-300">
              Built with support from <span className="font-medium">Microsoft for Startups</span>
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default PublicInsightsListPage;
